import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Container, Overline, Reveal, HairlineList } from "@/components/primitives";
import { submitPlaybook } from "@/lib/api";
import { useDocumentMeta } from "@/lib/meta";

const field =
  "w-full border border-input bg-transparent px-4 py-3 text-foreground placeholder:text-muted-foreground/70 focus:border-accent focus:outline-none transition-colors";

export default function Playbook() {
  useDocumentMeta({
    title: "For IT teams — the M365 playbook",
    description:
      "The IT team's guide to running Beyond the Box on your own Microsoft 365 tenant. Your data never leaves your estate — and your IT team can see exactly what it does.",
    path: "/playbook",
  });

  const [form, setForm] = useState({ name: "", firm: "", email: "", it_contact_email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.firm || !form.email) {
      toast.error("Please add your name, firm and email.");
      return;
    }
    setBusy(true);
    try {
      await submitPlaybook({
        name: form.name,
        firm: form.firm,
        email: form.email,
        it_contact_email: form.it_contact_email || null,
        message: form.message || null,
      });
      setDone(true);
      toast.success("We'll get the playbook to your IT team.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="playbook-page">
      {/* Hero */}
      <section className="border-b border-border">
        <Container className="py-20 md:py-28">
          <Reveal>
            <Overline accent>For IT teams · The M365 playbook</Overline>
            <h1 className="mt-6 max-w-4xl font-display text-4xl font-extrabold leading-[0.92] tracking-[-0.03em] text-foreground md:text-7xl">
              The IT team's guide to running Beyond the Box on your Microsoft 365 tenant.
            </h1>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-xl">
              Built on the Microsoft 365 you already control. Your data never leaves your estate.
              Your IT team can see exactly what it does — because we've nothing to hide.
            </p>
          </Reveal>
        </Container>
      </section>

      {/* Transparency framing + gate */}
      <section className="border-b border-border">
        <Container className="grid grid-cols-1 gap-14 py-20 md:py-24 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <div className="space-y-10">
                {[
                  {
                    t: "It's your tenant, not our cloud.",
                    d: "ComplianceMinder and CaseMinder run inside your own Microsoft 365 — SharePoint, Outlook, your data, your estate. We don't hold your data hostage because we never hold it.",
                  },
                  {
                    t: "Every permission, explained.",
                    d: "We tell your IT team exactly which Microsoft Graph scopes we request and why. No mystery permissions, no scope creep.",
                  },
                  {
                    t: "Auditable by design.",
                    d: "Your IT team can see the data flows. The playbook walks them through it. Try getting that from your current vendor.",
                  },
                  {
                    t: "The anti-black-box.",
                    d: "Your IT people are Microsoft experts who are tired of systems they can't get under the bonnet of. This is the opposite of that.",
                  },
                ].map((b, i) => (
                  <div key={i} className="border-l-2 border-accent pl-5">
                    <h3 className="font-display text-xl font-bold tracking-[-0.02em] text-foreground md:text-2xl">
                      {b.t}
                    </h3>
                    <p className="mt-3 leading-relaxed text-muted-foreground">{b.d}</p>
                  </div>
                ))}
              </div>
              <div className="mt-10">
                <span className="overline text-muted-foreground">What the playbook covers</span>
                <HairlineList
                  testid="playbook-covers-list"
                  items={[
                    "Tenant model, app registration and SSO",
                    "Microsoft Graph scopes — and the reason for each",
                    "Data residency: nothing leaves your estate",
                    "SharePoint structure and version control",
                    "Auth & security posture (SSO, MFA, sessions)",
                    "A high-level data-flow diagram your IT can audit",
                    "The config checklist to stand it up in your tenant",
                  ]}
                />
              </div>
            </Reveal>
          </div>

          {/* Gate */}
          <div className="lg:col-span-5 lg:col-start-8">
            {done ? (
              <div
                data-testid="playbook-success"
                className="flex h-full flex-col justify-center border border-border p-10 md:p-12"
              >
                <div className="flex h-12 w-12 items-center justify-center border border-accent text-accent">
                  <Check strokeWidth={1.8} className="h-6 w-6" />
                </div>
                <h2 className="mt-6 font-display text-3xl font-bold tracking-[-0.02em] text-foreground">
                  Request received.
                </h2>
                <p className="mt-4 leading-relaxed text-muted-foreground">
                  Thank you, {form.name.split(" ")[0] || "there"}. We'll get the playbook to your IT
                  team.
                </p>
                <div className="mt-8">
                  <Link
                    to="/"
                    data-testid="playbook-back-home"
                    className="inline-flex items-center gap-3 border border-foreground/40 px-6 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-foreground transition-colors hover:border-accent hover:text-accent"
                  >
                    Back to home <span>→</span>
                  </Link>
                </div>
              </div>
            ) : (
              <form data-testid="playbook-form" onSubmit={onSubmit} className="border border-border p-7 md:p-10">
                <span className="overline text-accent">Request the playbook</span>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  We'll send the technical playbook to your IT team. No public download — earned
                  technical proof, delivered to the right person.
                </p>
                <div className="mt-7 space-y-6">
                  <div>
                    <label className="overline mb-2 block text-muted-foreground">Your name *</label>
                    <input data-testid="playbook-name" className={field} value={form.name} onChange={update("name")} placeholder="Jane Partner" required />
                  </div>
                  <div>
                    <label className="overline mb-2 block text-muted-foreground">Firm *</label>
                    <input data-testid="playbook-firm" className={field} value={form.firm} onChange={update("firm")} placeholder="Firm name" required />
                  </div>
                  <div>
                    <label className="overline mb-2 block text-muted-foreground">Your email *</label>
                    <input data-testid="playbook-email" type="email" className={field} value={form.email} onChange={update("email")} placeholder="you@firm.law" required />
                  </div>
                  <div>
                    <label className="overline mb-2 block text-muted-foreground">
                      IT contact email
                    </label>
                    <input
                      data-testid="playbook-it-email"
                      type="email"
                      className={field}
                      value={form.it_contact_email}
                      onChange={update("it_contact_email")}
                      placeholder="Who should we send the technical playbook to?"
                    />
                  </div>
                  <div>
                    <label className="overline mb-2 block text-muted-foreground">Message (optional)</label>
                    <textarea data-testid="playbook-message" className={`${field} min-h-[100px] resize-y`} value={form.message} onChange={update("message")} placeholder="Anything specific your IT team wants to know?" />
                  </div>
                </div>
                <button
                  type="submit"
                  data-testid="playbook-submit"
                  disabled={busy}
                  className="mt-8 inline-flex w-full items-center justify-center gap-2 bg-primary px-6 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-primary-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
                >
                  {busy ? "Sending…" : "Request the playbook for your IT team"}
                </button>
              </form>
            )}
          </div>
        </Container>
      </section>
    </div>
  );
}
